import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Vector3 } from 'three'

import {
  ControlOptions,
  ModelControlsInterface,
} from '@src/components/adgroup-three-fiber/objects/hover-hitbox'
import { SessionMap } from '@src/firebase-util/types'

import {
  BuildingInterface,
  setBuilding,
  setByFlag,
} from '@store/actionSlices/building'
import {
  ThreeDViewState,
  resetThreeDView,
  setSelectedLevelId,
  setThreeDView,
} from '@store/actionSlices/threeDView'
import { setFilter } from '@store/actionSlices/unitFilter'
import { RouteKey } from '@store/constants'
import { ExtendedUnitFilterOptions, RootStateTypeExtra } from '@store/types'

import Model, { ModelRefProps } from '@components/adgroup-three-fiber/model'
import { ModelLoader } from '@components/adgroup-three-fiber/types'
import Container from '@components/container'
import DataHandler from '@components/data-handler'
import { FULFILLED_STATUS } from '@components/data-handler/data-handler'
import FilterPopup from '@components/filter-popup'
import ZoneDropdown from '@components/zone-dropdown'

import ThreeDSkeleton from '@pages/3d/3d-skeleton'

import { Level, useGetBuildingQuerySilent } from '@api/building'
import { StatusLabels } from '@api/config'
import { MappingBlockCollection } from '@api/interactive-plan'
import {
  Model as ModelApiInterface,
  ModelControlAction,
  ModelControl as ModelControlsApiInterface,
  Scene as SceneInterface,
  selectFromSceneListResult,
  useGetScenesQuery,
} from '@api/showcase-3d'

import useStateRef from '@utilities/use-state-ref'

import { ArrowSvg, FilterSvg } from '@svg/react'

import filterUnit from '@adUtilities/filter-unit'
import { Unit } from '@adUtilities/types/apartment'

import {
  ENABLE_PERFORMANCE_STATS,
  NO_FOCUS_TARGETS,
  PROPERTY_LABEL_PREFIX,
  SHOW_HIGHLIGHTED_LEVEL_INFO,
} from './constants'
import HighlightedLevelInfo from './highlighted-level-info'
import LevelExplorer, { SelectedLevelCard } from './level-explorer'

interface ModelViewInterface extends Omit<ModelApiInterface, 'loader'> {
  loader: ModelLoader
}

interface ADThreeDViewerProps {
  session: SessionMap | undefined
  unitFilter: ExtendedUnitFilterOptions
  projectName: string
  building: BuildingInterface
  showPrice: boolean
  statusLabels: StatusLabels
  storeBlocks: MappingBlockCollection
  threeDView: ThreeDViewState
}

function ADThreeDView({
  unitFilter,
  building,
  session,
  projectName,
  showPrice,
  statusLabels,
  storeBlocks,
  threeDView,
}: ADThreeDViewerProps) {
  const dispatch = useDispatch()
  const history = useHistory()

  const [activeScene, setActiveScene] = useState<SceneInterface>()

  const [models, setModels] = useState<ModelViewInterface[]>([])
  const [activeModel, setActiveModel] = useState('')
  const activeModelData = useMemo(
    () => models?.find((item) => item.id === activeModel),
    [models, activeModel]
  )

  const [modelControl, setModelControl] = useState<ModelControlsInterface>()

  const [highLightTarget, setHighLightTarget] = useState('')

  const [isFilterOpen, toggleFilter] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const { blockOrders, levels } = building

  const [firebasePosition, setFirebasePosition] = useState<
    Vector3 | undefined
  >()

  const [highlightedLevel, setHighlightedLevel] = useState('')
  const [propertyCount, setPropertyCount] = useState(0)
  const [renderedModelId, setRenderedModelId] = useState<string>()
  const isModelLoaded = renderedModelId === activeModel

  const { selectedLevelId } = threeDView

  const handleSelectLevel = useCallback(
    (levelId: string) => dispatch(setSelectedLevelId(levelId)),
    []
  )

  const modelRef = useRef<ModelRefProps>(null)
  const isFirstMount = useRef(true)
  const isOpeningLevelInBuilding = useRef(false)

  const scenesQueryResult = useGetScenesQuery(
    {
      projectIdOrName: projectName,
      include: 'models,models.control',
      limit: 1,
    },
    { selectFromResult: selectFromSceneListResult }
  )

  const buildingQueryResult = useGetBuildingQuerySilent({ projectName })

  const setBlockFocus = (focusOn: string) => {
    dispatch(
      setByFlag({
        flag: 'activeBlock',
        value: focusOn,
      })
    )
  }

  const blockOrdersRef = useStateRef(blockOrders)
  const levelsRef = useStateRef(levels)

  const canRenderModel = models.length > 0 && !!modelControl

  const hasLevelControls = useMemo(
    () =>
      !!activeModelData?.modelControl?.some(
        (control) => control.action === ModelControlAction.RedirectBuilding
      ),
    [activeModelData]
  )

  const canShowLevels = !isConnected && hasLevelControls && isModelLoaded
  const canFocusLevels =
    hasLevelControls && (!isConnected || !!session?.threeDView?.showLevels)
  const focusedLevelId = isConnected
    ? session?.threeDView?.selectedLevelId || ''
    : selectedLevelId
  const isLevelSelected = canFocusLevels && focusedLevelId !== ''

  const validateActionTargetId = useCallback<
    (blockId?: string, levelId?: string) => boolean
  >((blockId, levelId) => {
    const currentBlockOrders = blockOrdersRef.current
    const currentLevels = levelsRef.current

    let isValid = true

    if (currentBlockOrders?.length > 1 && !blockId) {
      isValid = false
    }
    if (blockId && !currentBlockOrders?.includes(blockId)) {
      isValid = false
    }
    if (!levelId || !currentLevels?.some((level) => level.level === levelId)) {
      isValid = false
    }

    return isValid
  }, [])

  const getHighlightControlLabel = useCallback(
    (actionTarget: string) => {
      const controls = Object.values(modelControl ?? {})
      const matchControl = controls.find(
        (control) => control?.actionTarget === actionTarget
      )
      return matchControl?.label || null
    },
    [modelControl]
  )

  const parseActionTargetId = useCallback(
    (actionTarget: string) => {
      const separator = '-'
      const splitted = actionTarget.trim().split(separator)
      const blockId = splitted.length > 1 ? splitted[0]?.trim() : undefined
      const levelId = splitted[splitted.length - 1]?.trim()
      const isValid = validateActionTargetId(blockId, levelId)

      return {
        levelId,
        blockId,
        isValid,
      }
    },
    [validateActionTargetId]
  )

  const levelDataByBlock = useCallback(
    (foundLevel: Level) =>
      building.activeBlock.length > 1
        ? foundLevel.data.filter(
            (unit) => unit.blockId === building.activeBlock
          )
        : foundLevel.data,
    [building.activeBlock]
  )

  const findRelevantLevel = useCallback(
    (levelName: string) =>
      levelsRef.current?.find((res) => levelName === res.level),
    []
  )

  const canUnitFilterByBlock = useCallback(
    (unit: Unit) =>
      building.activeBlock &&
      unit.blockId &&
      unit.blockId !== building.activeBlock,
    [building.activeBlock]
  )

  const isLevelMatchingFilter = useCallback(
    (levelId: string, blockId?: string) => {
      const foundLevel = findRelevantLevel(levelId)

      if (!unitFilter.apply || !foundLevel) {
        return false
      }

      return levelDataByBlock(foundLevel).some((unit: Unit) => {
        if (canUnitFilterByBlock(unit)) {
          return false
        }
        if (blockId && blockId !== unit.blockId) {
          return false
        }
        return filterUnit(unit, unitFilter, showPrice, statusLabels?.available)
      })
    },
    [
      unitFilter,
      findRelevantLevel,
      levelDataByBlock,
      canUnitFilterByBlock,
      showPrice,
      statusLabels,
    ]
  )

  const getLevelStatus = useCallback(
    (actionTarget?: string) => {
      if (!actionTarget || canFocusLevels) {
        return false
      }
      const { levelId, blockId, isValid } = parseActionTargetId(actionTarget)

      if (!isValid) {
        return false
      }

      if (highLightTarget === actionTarget) {
        return true
      }

      return isLevelMatchingFilter(levelId, blockId)
    },
    [parseActionTargetId, isLevelMatchingFilter, canFocusLevels]
  )

  const focusTargets = useMemo(() => {
    if (!canFocusLevels || (!isLevelSelected && !unitFilter.apply)) {
      return NO_FOCUS_TARGETS
    }

    const matchingControlIds = Object.values(modelControl ?? {}).flatMap(
      ({ id, action, actionTarget }) => {
        if (
          !id ||
          !actionTarget ||
          action !== ModelControlAction.RedirectBuilding
        ) {
          return []
        }
        const { levelId, blockId, isValid } = parseActionTargetId(actionTarget)
        if (!isValid) {
          return []
        }
        const isMatch = isLevelSelected
          ? levelId === focusedLevelId &&
            (!blockId || blockId === building.activeBlock)
          : isLevelMatchingFilter(levelId, blockId)
        return isMatch ? [id] : []
      }
    )

    return matchingControlIds.length ? matchingControlIds : NO_FOCUS_TARGETS
  }, [
    canFocusLevels,
    isLevelSelected,
    unitFilter.apply,
    modelControl,
    parseActionTargetId,
    focusedLevelId,
    building.activeBlock,
    isLevelMatchingFilter,
  ])

  const updateVisibleModel = (targetId: string) => {
    setModels((prevModels) =>
      prevModels.map((model) => ({
        ...model,
        visible: model.id === targetId,
      }))
    )
    dispatch(
      setThreeDView({
        activeModel: targetId,
        selectedLevelId: '',
      })
    )
  }

  const changeScene = useCallback(
    (targetIdArg: string) => {
      const targetId = targetIdArg?.trim()
      const isValid = models.some((model) => model.id === targetId)
      if (!isValid) {
        return
      }
      modelRef?.current?.startStaticLoader?.(targetId)
      setActiveModel(targetId)
      updateVisibleModel(targetId)
    },
    [updateVisibleModel, models]
  )

  const navigateToBuildingLevel = (levelId: string) => {
    isOpeningLevelInBuilding.current = true
    dispatch(
      setByFlag({
        flag: 'activeLevel',
        value: levelId,
      })
    )
    history.push(RouteKey.BUILDING)
  }

  const navigateToBuildingUnit = (levelId: string, unitName: string) => {
    dispatch(
      setByFlag({
        flag: 'activeUnit',
        value: unitName,
      })
    )
    navigateToBuildingLevel(levelId)
  }

  const activeBlockRef = useStateRef(building.activeBlock)
  const redirectToBuilding = useCallback(
    (actionTarget: string) => {
      const { levelId, blockId, isValid } = parseActionTargetId(actionTarget)
      if (!isValid) {
        return
      }
      if (blockId && activeBlockRef.current !== blockId) {
        dispatch(
          setByFlag({
            flag: 'activeBlock',
            value: blockId,
          })
        )
      }
      navigateToBuildingLevel(levelId || '')
    },
    [parseActionTargetId]
  )

  const canAction = useCallback(
    (control: Pick<ControlOptions, 'action' | 'actionTarget'>) =>
      !!(control?.action && control?.actionTarget),
    []
  )

  const handleModelOnClick = (obj: ControlOptions) => {
    if (obj?.action === ModelControlAction.ChangeScene && obj?.actionTarget) {
      changeScene(obj.actionTarget)
    }
    if (
      obj?.action === ModelControlAction.RedirectBuilding &&
      obj?.actionTarget
    ) {
      redirectToBuilding(obj.actionTarget)
    }
  }

  const handleModelControlOnFilter = (
    modelControls: ModelControlsInterface
  ) => {
    const modelControlMapped: ModelControlsInterface = {}
    Object.keys(modelControls).forEach((key) => {
      if (modelControls[key].id) {
        modelControlMapped[key] = {
          ...modelControls[key],
          hoverActive: canAction(modelControls[key])
            ? getLevelStatus(modelControls[key].actionTarget) || false
            : modelControls[key]?.hoverActive,
        }
      }
    })
    return modelControlMapped
  }

  const setModelControlOnClick = (
    modelControls: ModelControlsApiInterface[]
  ) => {
    const modelControlsMapped: ModelControlsInterface = {}

    modelControls.forEach((control) => {
      modelControlsMapped[control.name] = {
        id: control.actionTarget,
        label: control.label,
        actionTarget: control.actionTarget,
        action: control.action,
        hoverColor: control.hoverColor,
        position: control.position,
        rotation: control.rotation,
        scale: control.scale,
        hoverActive: canAction(control)
          ? getLevelStatus(control.actionTarget) || false
          : control.hoverActive,
        onClick: (arg) => handleModelOnClick(arg),
      }
    })

    return modelControlsMapped
  }

  const handleBackButton = () => {
    const foundActiveModel = models.find((model) => model.id === activeModel)
    if (!foundActiveModel?.prev) {
      history.push(RouteKey.VISION)
      return
    }

    const targetId = foundActiveModel.prev

    modelRef?.current?.startStaticLoader(targetId)
    setActiveModel(targetId)
    updateVisibleModel(targetId)
  }

  const handleModelTarget = (currentModel: ModelViewInterface) => {
    if (modelRef.current && isConnected) {
      modelRef.current.spawnCamera(
        new Vector3(...currentModel.position),
        new Vector3(...currentModel.target)
      )
    }
  }

  const mapSourceModelsToView = (
    models: ModelApiInterface[]
  ): ModelViewInterface[] =>
    models.map((item) => ({
      ...item,
      id: item.name,
      loader: {
        icon: item.loader.icon,
        bg: item.loader.bg,
        bar: {
          color: item.loader.color,
        },
      },
    }))

  const blockKeys = useMemo(
    () => (blockOrders.length > 1 ? blockOrders : Object.keys(storeBlocks)),
    [blockOrders, storeBlocks]
  )

  const blockOptions = useMemo(() => {
    if (blockOrders.length) {
      return blockOrders.map((item) => ({
        label: item,
        value: item,
      }))
    }

    return Object.keys(storeBlocks || {})?.map((item) => ({
      label: item,
      value: item,
    }))
  }, [blockOrders, storeBlocks])

  const onChangeZone = useCallback(
    (item: string) => {
      setBlockFocus(item)
      handleSelectLevel('')
    },
    [handleSelectLevel]
  )

  const resetHighlightedLevelInfo = (): void => {
    setHighlightedLevel('')
    setPropertyCount(0)
  }

  const setHighlightedLevelInfo = useCallback(
    (actionTarget: string) => {
      if (!activeModelData?.showLevelInfoOnHover) {
        return
      }

      if (!actionTarget) {
        resetHighlightedLevelInfo()
        return
      }

      const { levelId, blockId } = parseActionTargetId(actionTarget)
      if (!levelId) {
        return
      }

      const customLabel = getHighlightControlLabel(actionTarget)
      setHighlightedLevel(`${customLabel || PROPERTY_LABEL_PREFIX} ${levelId}`)

      const foundLevel = findRelevantLevel(levelId)
      if (!foundLevel) {
        return
      }

      let units = blockId
        ? foundLevel.data.filter((unit) => unit.blockId === blockId)
        : foundLevel.data

      if (unitFilter.apply) {
        units = units.filter((unit: Unit) =>
          filterUnit(unit, unitFilter, showPrice, statusLabels?.available)
        )
      }

      setPropertyCount(units.length)
    },
    [
      unitFilter,
      findRelevantLevel,
      showPrice,
      parseActionTargetId,
      modelControl,
    ]
  )

  const loaderInitialized = useRef(false)
  useEffect(() => {
    if (loaderInitialized.current || !canRenderModel || !activeModel) {
      return
    }
    modelRef?.current?.startStaticLoader(activeModel)
    loaderInitialized.current = true
  }, [canRenderModel])

  useEffect(() => {
    if (!session?.connected) {
      return
    }
    const firebaseActiveBlock = session?.building?.activeBlock
    setBlockFocus(firebaseActiveBlock)
  }, [session?.connected, session?.building?.activeBlock])

  useEffect(() => {
    if (session?.connected) {
      setIsConnected(true)
      return
    }
    setIsConnected(false)
  }, [session?.connected])

  useEffect(() => {
    if (!session?.connected) {
      return
    }
    const firebaseFilter = session?.building?.unitFilter
    if (firebaseFilter) {
      dispatch(setFilter(firebaseFilter))
    }
  }, [session?.connected, session?.building?.unitFilter])

  useEffect(() => {
    if (!session?.connected) {
      return
    }

    const firebaseActiveModel = session?.threeDView?.activeModel
    if (!firebaseActiveModel || firebaseActiveModel === activeModel) {
      return
    }
    modelRef?.current?.startStaticLoader(firebaseActiveModel)
    setActiveModel(firebaseActiveModel)
    updateVisibleModel(firebaseActiveModel)
  }, [session?.connected, session?.threeDView?.activeModel])

  useEffect(() => {
    if (!session?.connected) {
      return
    }
    const position = session?.threeDView?.position ?? []
    if (position && position.filter((pos) => pos !== 0).length === 3) {
      setFirebasePosition(new Vector3(position[0], position[1], position[2]))
    }
  }, [session?.connected, session?.threeDView?.position])

  useEffect(() => {
    if (session?.connected) {
      setHighLightTarget(session?.threeDView?.highLightTarget)
    }
  }, [session?.connected, session?.threeDView?.highLightTarget])

  useEffect(() => {
    if (modelRef.current && firebasePosition) {
      modelRef.current.setPosition(firebasePosition)
    }
  }, [firebasePosition])

  useEffect(() => {
    if (unitFilter && modelControl) {
      setModelControl(handleModelControlOnFilter(modelControl))
    }
  }, [unitFilter, highLightTarget, building.activeBlock, canFocusLevels])

  useEffect(() => {
    if (!models?.length) {
      return
    }

    const currentModel = models.find((model) => model.visible)
    if (!currentModel) {
      return
    }

    setActiveModel(currentModel.id)
    setModelControl(setModelControlOnClick(currentModel.modelControl))
    handleModelTarget(currentModel)
  }, [models, isConnected])

  useEffect(() => {
    if (!activeScene?.models?.length) {
      return
    }

    setModels(() => {
      const mappedModels = mapSourceModelsToView(activeScene.models)

      if (threeDView.activeModel) {
        return mappedModels.map((model) => ({
          ...model,
          visible: model.id === threeDView.activeModel,
        }))
      }

      return mappedModels
    })
  }, [activeScene, threeDView.activeModel])

  useEffect(() => {
    const { isLoaded, status, scenes } = scenesQueryResult

    if (!isLoaded || status !== FULFILLED_STATUS || !scenes?.list?.length) {
      return
    }

    setActiveScene(scenes.list[0])
  }, [
    scenesQueryResult.isLoaded,
    scenesQueryResult.status,
    scenesQueryResult.scenes,
  ])

  useEffect(() => {
    const {
      isLoaded,
      status,
      building: buildingData,
      blockOrders: blockOrdersData,
    } = buildingQueryResult

    if (!isLoaded || status !== FULFILLED_STATUS || !buildingData?.length) {
      return
    }

    const isBackBtnNavigation =
      history.location.state?.isBackBtnNavigation || false
    const localActiveBlock =
      isBackBtnNavigation && blockOrdersData.includes(building.activeBlock)
        ? building.activeBlock
        : blockOrdersData[0]
    const activeBlock = session?.connected
      ? session?.building?.activeBlock
      : localActiveBlock

    dispatch(
      setBuilding({
        ...building,
        levels: buildingData,
        blockOrders: blockOrdersData,
        activeBlock: activeBlock || '',
      })
    )
  }, [buildingQueryResult.isLoaded, buildingQueryResult.status])

  useEffect(() => {
    setHighlightedLevelInfo(highLightTarget)
  }, [highLightTarget])

  useEffect(() => {
    if (!isFirstMount.current || session?.connected) {
      return
    }

    const isBackBtnNavigation =
      history.location.state?.isBackBtnNavigation || false

    if (
      !isBackBtnNavigation &&
      (threeDView.activeModel || threeDView.selectedLevelId)
    ) {
      dispatch(resetThreeDView())
    }

    isFirstMount.current = false
  }, [history.location.state, session?.connected])

  useEffect(
    () => () => {
      if (session?.connected) {
        dispatch(resetThreeDView())
      }
    },
    [session?.connected]
  )

  useEffect(
    () => () => {
      if (!isOpeningLevelInBuilding.current) {
        dispatch(setSelectedLevelId(''))
      }
    },
    []
  )

  return (
    <Container>
      <DataHandler
        payloads={[
          {
            name: 'scenesQueryResult',
            payload: {
              ...scenesQueryResult,
              data: scenesQueryResult.scenes,
            },
          },
          {
            name: 'buildingQueryResult',
            payload: {
              ...buildingQueryResult,
              data: building.levels,
            },
          },
        ]}
        skeletonFrame={<ThreeDSkeleton />}
      >
        <div
          className={`absolute left-5 top-5 z-30 ${
            isConnected ? 'invisible' : ''
          }`}
        >
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackButton}
              type="button"
              className="rounded bg-white p-1 drop-shadow-40"
            >
              <ArrowSvg className="h-8 w-8" strokeColor="#000" />
            </button>
            {models.find((m) => m.visible)?.hasFiltering && (
              <button
                data-testid="toggle-filter"
                onClick={() => toggleFilter(!isFilterOpen)}
                type="button"
                className="rounded bg-white p-2.5 drop-shadow-40"
              >
                <FilterSvg className="h-5 w-5" />
              </button>
            )}
            {activeModelData?.hasFiltering && blockKeys.length > 1 && (
              <ZoneDropdown
                activeZone={building.activeBlock}
                zoneOptions={blockOptions}
                onChange={onChangeZone}
              />
            )}
          </div>
        </div>
        {canRenderModel && (
          <Model
            models={models}
            modelControl={modelControl}
            ref={modelRef}
            enablePan={false}
            color="skyblue"
            disableCameraLoadingCntrl={isConnected}
            getHighlightedTarget={setHighlightedLevelInfo}
            enablePerformanceStats={ENABLE_PERFORMANCE_STATS === 'true'}
            performanceStatsClassName="absolute bottom-5 left-5 z-30"
            disableInteraction={canFocusLevels}
            focusTargets={focusTargets}
            cameraFocusTargets={
              isLevelSelected ? focusTargets : NO_FOCUS_TARGETS
            }
            onModelReady={setRenderedModelId}
          />
        )}
        <FilterPopup
          isOpen={isFilterOpen}
          toggle={toggleFilter}
          hideLevelOption
        />
        {canShowLevels && (
          <LevelExplorer
            selectedLevelId={selectedLevelId}
            onSelectLevel={handleSelectLevel}
            onSeeFloorplateView={navigateToBuildingLevel}
            onSelectUnit={navigateToBuildingUnit}
          />
        )}
        {isConnected && isLevelSelected && (
          <SelectedLevelCard levelId={focusedLevelId} />
        )}
        {SHOW_HIGHLIGHTED_LEVEL_INFO &&
          highlightedLevel &&
          !isLevelSelected && (
            <div className="absolute right-8 top-8 z-1">
              <HighlightedLevelInfo
                highlightedLevel={highlightedLevel}
                propertyCount={propertyCount}
              />
            </div>
          )}
      </DataHandler>
    </Container>
  )
}

const mapStateToProps = ({
  unitFilter,
  firestore: { session },
  projectIdentity: { projectName },
  building,
  projectConfig: { showPrice, statusLabels },
  interactivePlan,
  threeDView,
}: RootStateTypeExtra) => ({
  unitFilter,
  session,
  projectName,
  building,
  showPrice,
  statusLabels,
  storeBlocks: interactivePlan.blocks,
  threeDView,
})

export default connect(mapStateToProps)(ADThreeDView)
